// Khung 390×844 (UI_SPEC §1): MỘT hàm fitFrame() tính mọi biến; JS chỉ đưa --khung-k (suy từ bề
// ngang), chiều cao do CSS quyết. Safe-area đo bằng phần tử probe; test headless giả lập bằng
// `?sa=59,34[,left,right]` hoặc `window.__sa(top, bottom, left?, right?)`.

export const FRAME_W = 390;
export const FRAME_H = 844;
const PHONE_W = 480;
const TOPBAR_D = 113;
const NAV_D = 70;
/** Dải thân thiết kế: 844 − 113 − 93 (nav có vạch home). */
const BODY_D = FRAME_H - TOPBAR_D - 93;

interface Insets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

let forced: Insets | null = null;
let probe: HTMLDivElement | null = null;

function root(): HTMLElement {
  return document.documentElement;
}

function setPx(k: string, v: number): void {
  root().style.setProperty(k, `${v.toFixed(2)}px`);
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

function ensureProbe(): HTMLDivElement {
  if (probe) return probe;
  const p = document.createElement('div');
  p.setAttribute('aria-hidden', 'true');
  p.style.cssText =
    'position:fixed;left:0;top:0;width:0;height:0;visibility:hidden;pointer-events:none;box-sizing:content-box;' +
    'padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px);' +
    'padding-left:env(safe-area-inset-left,0px);padding-right:env(safe-area-inset-right,0px);';
  document.body.appendChild(p);
  probe = p;
  return p;
}

function readInsets(): Insets {
  if (forced) return forced;
  const cs = getComputedStyle(ensureProbe());
  return {
    top: parseFloat(cs.paddingTop) || 0,
    bottom: parseFloat(cs.paddingBottom) || 0,
    left: parseFloat(cs.paddingLeft) || 0,
    right: parseFloat(cs.paddingRight) || 0,
  };
}

export function fitFrame(): void {
  const vv = window.visualViewport;
  const w = Math.round(vv ? vv.width : window.innerWidth);
  const h = Math.round(vv ? vv.height : window.innerHeight);
  const phone = w <= PHONE_W;
  const k = Math.max(0.4, phone ? w / FRAME_W : Math.min((w - 28) / FRAME_W, (h - 28) / FRAME_H, 1.3));
  root().style.setProperty('--khung-k', k.toFixed(5));
  if (isStandalone()) root().style.removeProperty('--vvh');
  else root().style.setProperty('--vvh', `${h}px`);

  const sa = readInsets();
  setPx('--sa-top', sa.top / k);
  setPx('--sa-bottom', sa.bottom / k);
  setPx('--sa-left', sa.left / k);
  setPx('--sa-right', sa.right / k);

  // Đọc lại hộp CSS vừa dựng (px thật) rồi quy về px thiết kế.
  const box = document.getElementById('app-root')?.getBoundingClientRect().height ?? 0;
  const frameH = box > 1 ? box / k : h / k;
  setPx('--frame-h', frameH);

  const tb = document.querySelector<HTMLElement>('.shell-topbar');
  const nv = document.querySelector<HTMLElement>('.shell-nav');
  const row1 = document.querySelector<HTMLElement>('.shell-topbar .tb-row');
  const topbarH = tb && tb.offsetHeight > 0 ? tb.offsetHeight : TOPBAR_D + Math.max(0, sa.top / k - 12);
  const navH = nv && nv.offsetHeight > 0 ? nv.offsetHeight : Math.max(NAV_D, 59 + Math.max(7, sa.bottom / k));
  setPx('--topbar-h', topbarH);
  setPx('--body-top', topbarH);
  // Trang con phủ từ đáy hàng 1 của topbar (đo thật, không chép số).
  if (row1 && row1.offsetHeight > 0) setPx('--subpage-top', row1.offsetHeight);

  const bodyH = Math.max(120, frameH - topbarH - navH);
  const kb = Math.min(1, Math.max(0.5, bodyH / BODY_D));
  const dy = topbarH - kb * TOPBAR_D;
  setPx('--fit-dy', dy);
  root().style.setProperty('--fit-k', kb.toFixed(4));
  setPx('--screen-h', (frameH - dy) / kb);
  setPx('--fit-nav', navH / kb);

  const c = Math.max(1, frameH / FRAME_H);
  root().style.setProperty('--cv-c', c.toFixed(4));
  setPx('--cv-x', (FRAME_W - FRAME_W * c) / 2);
  setPx('--cv-y', (frameH - FRAME_H * c) / 2);
}

function parseSaQuery(): Insets | null {
  const q = new URLSearchParams(location.search).get('sa');
  if (!q) return null;
  const n = q.split(',').map(Number);
  if (n.length < 2 || !n.every((x) => Number.isFinite(x))) return null;
  return { top: n[0] ?? 0, bottom: n[1] ?? 0, left: n[2] ?? 0, right: n[3] ?? 0 };
}

/** Gắn mọi đường refit (§1.5): resize, orientationchange, pageshow, visibilitychange, visualViewport, load. */
export function installFit(): void {
  forced = parseSaQuery();
  let raf = 0;
  const refit = (): void => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(fitFrame);
  };
  fitFrame();
  window.addEventListener('resize', refit, { passive: true });
  window.addEventListener('orientationchange', refit, { passive: true });
  window.addEventListener('pageshow', refit, { passive: true });
  window.addEventListener('load', refit, { passive: true });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refit();
  });
  window.visualViewport?.addEventListener('resize', refit, { passive: true });
  window.visualViewport?.addEventListener('scroll', refit, { passive: true });
  // PWA standalone: viewport lúc boot chưa ổn định.
  requestAnimationFrame(fitFrame);
  setTimeout(fitFrame, 250);
  setTimeout(fitFrame, 1000);
  (window as unknown as { __sa: (t: number | null, b?: number, l?: number, r?: number) => void }).__sa = (
    t,
    b = 0,
    l = 0,
    r = 0,
  ) => {
    forced = t === null ? null : { top: t, bottom: b, left: l, right: r };
    fitFrame();
  };
}
